import { $http } from '@/common/api/http.service';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import timezoneApi from '@/common/api/timezone.api';
import { getUrlFromCanonical } from '@/common/core';
import { crud } from '@/common/crud';
import { useAuthFlow } from '@/common/workflowEngine/useAuthFlow';
import { useChargeItemFlow } from '@/common/workflowEngine/useChargeItemFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { Encounter, Observation, Procedure, Questionnaire, QuestionnaireAnswerOption, Reference, Task } from '@/fhirworks';
import ProcedureModifier from '@/fhirworks/ProcedureModifier';
import { useAuthStore } from '@/stores/auth';
import axios from 'axios';
import { addDays, format, sub } from 'date-fns';
import uniqBy from 'lodash/uniqBy';
import { v4 as uuidv4 } from 'uuid';

const { postTransaction, getReference } = useWorkflow();
// Helper functions
const getTasksForQuestionnaireResponse = async function (id) {
    let task = null;
    let subTasks = null;
    await $httpFhirApi
        .get('Task?focus=' + id + '&_include=based-on:Task&_revinclude=Task:part-of')
        .then((result) => {
            task = result.data.find((item) => item.focus?.id === id && item.code.codeZero === 'form');
            subTasks = result.data.filter((item) => item.id !== task.id);
        })
        .catch(() => {
            task = null;
        });

    if (subTasks?.some((item) => item.groupTask?.id === task.id)) {
        task.temp.groupTask = { id: task.id, resourceType: 'Task' };
    }

    return [task, subTasks];
};

// Helper function to check if claims reference a specific form
const hasClaimsForForm = (claims, questionnaireResponseId) => {
    return claims.some((claim) => {
        return (
            claim.resourceType === 'Claim' &&
            claim.status === 'active' &&
            claim.supportingInfo?.some((info) => info.code?.text && info.code.text.includes(`QuestionnaireResponse/${questionnaireResponseId}`))
        );
    });
};

const populateSignerReviewerTask = function (task, owner, action) {
    let assignment = new Task();
    assignment.for = task.for;
    assignment.basedOn = task.basedOn;
    assignment.groupTask = task.groupTask || task.temp.groupTask;
    assignment.encounter = task.encounter;
    assignment.intent = 'unknown';
    assignment.status = 'accepted';
    assignment.authoredOn = new Date().toISOString();
    assignment.kanban = { priority: Date.now().toString() };
    assignment.code = { text: 're: ' + task.code.text, coding: [{ code: 'assignment', display: action.charAt(0).toUpperCase() + action.slice(1), version: action }] };
    assignment.restriction = { period: { start: new Date().toISOString(), end: addDays(new Date(), 7).toISOString() } };
    assignment.partOf = [{ id: task.id, resourceType: 'Task' }];

    // if it's a group note.  We want the signature/reviewer task to be connected to the main task, not the patients individual task
    if (assignment.groupTask) {
        assignment.partOf = [{ id: task.basedOn.find((item) => item.resourceType === 'Task')?.id, resourceType: 'Task' }];
    }

    assignment.owner = owner;
    assignment.focus = task.focus || undefined;
    return assignment;
};

const getSubtasks = async function (taskId) {
    let subTasks = null;
    await $httpFhirApi.get('Task?part-of=' + taskId).then((result) => {
        subTasks = result.data;
    });

    return subTasks;
};

// unlock managed items when making a standalone copy
const unlockFormItems = function (item) {
    item.forEach((i) => {
        i.locked = undefined;
        if (i.item.length) unlockFormItems(i.item);
    });
};

const findQuestionnaireItem = function (questionnaire, predicate) {
    for (const item of questionnaire.item || []) {
        if (predicate(item)) return item;
        if (item.item?.length) {
            const found = findQuestionnaireItem(item, predicate);
            if (found) return found;
        }
    }
    return null;
};

const filterQuestionnaireItems = function (questionnaire, predicate, results = []) {
    for (const item of questionnaire.item || []) {
        if (predicate(item)) results.push(item);
        if (item.item?.length) {
            filterQuestionnaireItems(item, predicate, results);
        }
    }
    return results;
};

const findQuestionnaireItemByLinkId = (questionnaire, linkId) => findQuestionnaireItem(questionnaire, (item) => item.linkId === linkId);

const filterQuestionnaireItemByLinkId = (questionnaire, linkId) => filterQuestionnaireItems(questionnaire, (item) => item.linkId === linkId);

const loopItems = function (items, questionnaire, callback) {
    items.forEach((i) => {
        let qItem = findQuestionnaireItemByLinkId(questionnaire, i.linkId);
        if (!qItem) return;
        if (qItem?.type === 'question') {
            callback(i, true);
        } else if (i.item.length) {
            loopItems(i.item, callback);
        } else {
            callback(i);
        }
    });
};

// extract QuestionnaireResponse to observations
const extractQuestionnaireResponse = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    let baseUrl = getUrlFromCanonical(data.questionnaireResponse.questionnaire).fullUrl;
    let questionnaire = (
        await $httpFhirApi.get(
            baseUrl + '&_elements=Questionnaire.item.linkId,Questionnaire.item.type,Questionnaire.item.code,Questionnaire.item.item.linkId,Questionnaire.item.item.type,Questionnaire.item.item.code',
        )
    ).data[0];

    const effective = new Date().toISOString();

    const disabledIds = data.disabledItems?.filter((e) => typeof e === 'string') || [];
    const disabledObjects = data.disabledItems?.filter((e) => typeof e !== 'string') || [];
    let valueTypes = ['Quantity', 'CodeableConcept', 'string', 'boolean', 'integer', 'Range', 'Ratio', 'SampledData', 'time', 'dateTime', 'Period', 'Attachment'];

    const removeDuplicates = (duplicates) => {
        const flag = {};
        const unique = [];
        duplicates.forEach((elem) => {
            if (!flag[elem.bnValue]) {
                flag[elem.bnValue] = true;
                unique.push(elem);
            }
        });

        return unique;
    };
    const addObservations = (item, qItem, qResponse) => {
        let disabledItemsArray = [...disabledIds];
        if (disabledItemsArray?.includes(item.linkId)) return;
        let observations = [];

        // If this is a boolean type field and there is no value (false)
        // insert the boolean: false so that it is stored and can be used
        // when getting the last stored value.
        if (qItem.type === 'boolean') {
            let currentValue = item.answer?.[0]?.value;
            if (!currentValue) {
                item.answer = { value: { boolean: false } };
            }
        }

        // grab only unique values so duplicates are removed
        // for some reason duplicates can be created.  So filter those here
        let itemAnswers = removeDuplicates(item.answer);
        for (const answerVal of itemAnswers) {
            let answerValue = answerVal?.renderingXhtml ? { string: answerVal.renderingXhtml } : answerVal.value;
            let performer = item.auditUser || qResponse.author;

            // answer needs to have a value as well as be of a type that observation supports
            if ((qItem.code.length || qItem.linkId.includes('score-')) && answerValue && valueTypes.some((type) => (type.toString() === 'boolean' ? true : answerValue[type.toString()]))) {
                let scoreCode = [{ system: 'http://fhir.bestnotes.com/scoreField', code: qItem.linkId }];
                let observation = new Observation({
                    status: 'final',
                    subject: qResponse.subject,
                    derivedFrom: [getReference(data.questionnaireResponse)],
                    encounter: qResponse.encounter,
                    code: {
                        coding: qItem.code?.length ? qItem.code : scoreCode,
                    },
                    effective: {
                        instant: effective,
                    },
                    value: answerValue,
                    performer: [performer],
                });

                let entry = crud.post(observation);

                transaction.push(entry);
                observations.push(entry.fullUrl);
            }
        }

        return observations;
    };

    let count = 0;
    let transactionList = [];
    loopItems(data.questionnaireResponse.item, questionnaire, (item, isQuestion = false) => {
        let qItem = findQuestionnaireItemByLinkId(questionnaire, item.linkId);

        // If a field set.  Loop over items and track observations, so we can set hasMember of main observation
        if (isQuestion) {
            let observations = [];

            loopItems(item.item, questionnaire, (item2) => {
                let disabledObj = disabledObjects?.find((e) => e.linkId === item2.linkId);
                if (disabledObj) {
                    if (count === disabledObj.index) {
                        return;
                    }
                    count++;
                }
                let qItem2 = findQuestionnaireItemByLinkId(questionnaire, item2.linkId);
                observations = observations.concat(addObservations(item2, qItem2, data.questionnaireResponse));
            });

            // observations is a list of fullUrl urn's map them to reference type with uri
            let members = observations.map((uri) => {
                if (uri) {
                    return { uri: uri };
                }
            });
            members = members.filter((e) => e);

            if (members.length) {
                let observation = new Observation({
                    status: 'final',
                    subject: data.questionnaireResponse.subject,
                    derivedFrom: [getReference(data.questionnaireResponse)],
                    encounter: data.questionnaireResponse.encounter,
                    hasMember: members,
                    code: {
                        coding: qItem.code,
                    },
                    effective: {
                        instant: effective,
                    },
                    performer: [data.questionnaireResponse.author],
                });

                transaction.push(crud.post(observation));
            }
        } else {
            // regular item,  Loop through answers and create observations
            addObservations(item, qItem, data.questionnaireResponse);
        }

        if (transaction.length > 40) {
            transactionList.push(transaction);
            transaction = [];
        }
    });

    transactionList.push(transaction);

    if (existingTransaction === false) {
        return await Promise.all(transactionList.map((transaction) => postTransaction(transaction)));
    }

    return transactionList;
};

const removeObservations = async function (qResponse, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let observations = (await $httpFhirApi.get('/Observation?subject=' + qResponse.subject.id + '&derived-from=' + qResponse.id)).data;

    observations.forEach((obs) => {
        transaction.push(crud.delete(obs));
    });

    return transaction;
};

const status = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    // set status
    data.questionnaireResponse.status = data.status;
    // remove signatures if required
    if (data.status === 'in-progress') {
        data.questionnaireResponse.reviewer?.forEach((item) => {
            item.when = undefined;
            item.context = undefined;
            item.format = undefined;
            item.signature = undefined;
            item.ip = undefined;
            item.user = undefined;
            item.credentials = undefined;
            item.jobTitle = undefined;
        });
    }
    // patch questionnaireResponse
    transaction.push(crud.patch(data.questionnaireResponse));

    // determine if this status change should propagate to related tasks
    if (['in-progress', 'completed'].includes(data.status)) {
        // update status of associated tasks
        const collaboratorTasks = data.assignments?.filter((task) => ['author', 'editor', 'viewer'].includes(task.code.coding[0].version)) || [];
        const groupParentTasks = data.assignments?.filter((task) => ['form'].includes(task.code.coding[0].code)) || [];
        // because when we unlock and lock a form, we change the status for all subtasks (patients) of the group note
        // we can assume we can change the status for the parent overall task for the whole form.
        [data.task, ...collaboratorTasks, ...groupParentTasks]
            .filter((task) => task)
            .forEach((task) => {
                task.status = data.status;
                if (data.status === 'completed') {
                    if (!task.executionPeriod) {
                        task.executionPeriod = { end: '' };
                    }
                    task.executionPeriod.end = new Date().toISOString();
                } else if (data.status === 'in-progress') {
                    // reset executionPeriod
                    if (!task.executionPeriod) {
                        task.executionPeriod = { start: new Date().toISOString() };
                    } else task.executionPeriod.end = undefined;
                }
                transaction.push(crud.patch(task));
            });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const signerReviewer = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    let isLockedQuestionnaire = data.questionnaireResponse?.status === 'completed';

    // assign tasks when form is locked
    if (data.action === 'assign') {
        data.questionnaireResponse.reviewer.forEach((item) => {
            const action = item.type === 'sign' ? 'signature' : 'review';
            transaction.push(crud.post(populateSignerReviewerTask(data.task, item.who, action)));
        });
    }

    // update signer/reviewer list and corresponding tasks
    else if (data.action === 'update') {
        /**
         * We want to be sure that a reviewer can only exist once as either a 'Signer' or 'Reviewer'
         * Replace/Update changed reviewers
         * Add new reviewers
         * Removal of orphaned users/tasks will happen later
         */

        data.reviewer.forEach((newReviewer) => {
            // remove reviewer and corresponding task if they already exist for another type
            const index = data.questionnaireResponse.reviewer.findIndex((oldReviewer) => newReviewer.who.id === oldReviewer.who.id && newReviewer.type !== oldReviewer.type);
            if (index > -1) {
                data.questionnaireResponse.reviewer.splice(index, 1);
                const oldType = newReviewer.type === 'sign' ? 'review' : 'signature';
                const task = data.assignments.find((task) => task.owner.id === newReviewer.who.id && oldType === task.code.coding[0].version);
                if (task && isLockedQuestionnaire) transaction.push(crud.delete(task));
            }
            // add/update reviewer and task if not already set for this type
            if (!data.questionnaireResponse.reviewer.find((oldReviewer) => newReviewer.who.id === oldReviewer.who.id && newReviewer.type === oldReviewer.type)) {
                data.questionnaireResponse.reviewer = newReviewer;
                const newType = newReviewer.type === 'sign' ? 'signature' : 'review';
                if (isLockedQuestionnaire) transaction.push(crud.post(populateSignerReviewerTask(data.task, newReviewer.who, newType)));
            }
        });

        /**
         * Now we clean up any orphaned reviewers (not in the new list)
         * including the associated tasks
         */
        data.questionnaireResponse.reviewer = data.questionnaireResponse.reviewer.filter((oldReviewer) => {
            const activeReviewer = !!data.reviewer.find((newReviewer) => newReviewer.who.id === oldReviewer.who.id);
            if (!activeReviewer) {
                const task = data.assignments.find((task) => task.owner.id === oldReviewer.who.id && ['signature', 'review'].includes(task.code.coding[0].version));
                if (task && isLockedQuestionnaire) transaction.push(crud.delete(task));
            }
            return activeReviewer;
        });

        // patch questionnaireResponse with updated signers/reviewers
        transaction.push(crud.patch(data.questionnaireResponse));
        data.questionnaireResponse.setObjectSaved();
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const removeAssignments = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    const tasks = data.assignments?.filter((task) => data.type.includes(task.code.coding[0]?.version)) || [];
    tasks.forEach((task) => {
        transaction.push(crud.delete(task));
    });

    return transaction;
};
const extractChargeItemResources = (response) => {
    let resources = [];
    if (response.data && response.data.entry && response.data.entry.length > 0) {
        resources = response.data.entry.map((entry) => entry.resource);
    } else if (response.data && Array.isArray(response.data)) {
        resources = response.data;
    } else if (response.data && response.data.resourceType) {
        resources = [response.data];
    }
    return resources;
};

// Deletes ChargeItems and authorization tracking for a QuestionnaireResponse
const cleanupChargeItemsForQuestionnaireResponse = async (questionnaireResponseId, encounterId = null, existingTransaction = false, isGroupEncounter = false) => {
    let transaction = existingTransaction || [];

    const { removeFormChargeItems } = useChargeItemFlow();
    const { removeAuthTrackingForChargeItems } = useAuthFlow();

    // Check if there are Claims referencing this form before deciding what to delete
    let hasSubmittedClaims = false;
    let encountersWithClaims = new Set(); // Track which encounters have claims for group encounters

    // Use encounter-based claim search if available
    if (encounterId) {
        let encounterIdsToCheck = [encounterId];

        // For group encounters, also check parent encounters
        if (isGroupEncounter) {
            const allEncounterIds = await getGroupEncounterIds(encounterId, []);
            encounterIdsToCheck = allEncounterIds;
        }

        // Check all encounters for claims - logic differs for group vs individual
        for (const encounterIdToCheck of encounterIdsToCheck) {
            const claimResponse = await $httpFhirApi.get(`Claim?encounter=${encounterIdToCheck}`);
            const claims = claimResponse.data?.entry?.map((e) => e.resource) || claimResponse.data || [];

            // Check if any claims reference this form via supportingInfo
            const hasClaimsForThisForm = hasClaimsForForm(claims, questionnaireResponseId);

            if (hasClaimsForThisForm) {
                if (isGroupEncounter) {
                    encountersWithClaims.add(encounterIdToCheck);
                }
                hasSubmittedClaims = true;

                // For individual encounters, we can break early since any claim means skip all cleanup
                if (!isGroupEncounter) {
                    break;
                }
            }
        }
    } else {
        hasSubmittedClaims = false;
    }

    // For individual encounters with claims, skip all cleanup
    // For group encounters, we'll handle cleanup per encounter below
    if (!isGroupEncounter && hasSubmittedClaims) {
        return transaction;
    }

    // Search for ChargeItems that reference our specific form
    let chargeItems = [];
    const chargeItemResponse = await $httpFhirApi.get(`ChargeItem?supportingInformation=QuestionnaireResponse/${questionnaireResponseId}`);
    chargeItems = chargeItemResponse.data?.entry?.map((e) => e.resource) || chargeItemResponse.data || [];

    if (chargeItems.length > 0) {
        // Determine which charge items to delete based on encounter type and claim status
        let chargeItemsToDelete = chargeItems;
        if (isGroupEncounter && hasSubmittedClaims) {
            // For group encounters with claims: only delete charge items for encounters without claims
            chargeItemsToDelete = chargeItems.filter((chargeItem) => {
                const chargeItemEncounterId = chargeItem.context?.id;
                return !chargeItemEncounterId || !encountersWithClaims.has(chargeItemEncounterId);
            });
        }

        if (chargeItemsToDelete.length > 0) {
            // Get the charge item and patient IDs of ChargeItems to be deleted
            const chargeItemIds = chargeItemsToDelete.map((ci) => ci.id);
            const patientIdsFromChargeItems = chargeItemsToDelete.map((ci) => ci.subject?.id || ci.subject?.reference?.split('/').pop()).filter(Boolean);

            if (patientIdsFromChargeItems.length > 0) {
                // Remove duplicates and join with commas for group encounters
                const uniquePatientIds = [...new Set(patientIdsFromChargeItems)];
                const patientId = uniquePatientIds.join(',');

                // Remove authorization tracking for these ChargeItems
                transaction = await removeAuthTrackingForChargeItems(chargeItemIds, patientId, transaction);
            }

            // Remove the ChargeItems themselves
            transaction = await removeFormChargeItems(chargeItemIds, transaction);
        }
    }
    return transaction;
};

// find per-diem procedure by following encounter partOf chain
const findPerDiemProcedureFromEncounters = async (data) => {
    // Try to get the entire partOf chain starting from the active encounter
    if (data.activeEncounter?.id) {
        try {
            // Start with the active encounter and include all partOf encounters recursively
            const encounterResponse = await $httpFhirApi.get(
                `Encounter?id=${data.activeEncounter.id}&_include:recurse=Encounter:part-of&_include=Encounter:healthcareService&_include:iterate=HealthcareService:perDiemProcedure`,
            );
            const resources = encounterResponse.data;

            // Find perDiemProcedure from all the included resources
            const perDiemProcedure = resources.find((item) => item?.resourceType === 'HealthcareService' && !item?.isProgram);

            if (perDiemProcedure) {
                return perDiemProcedure;
            }
        } catch (error) {
            console.error('Error fetching encounter partOf chain:', error);
        }
    }

    return null;
};

// function to check if session is associated with per-diem program and has per-diem procedure codes
async function shouldSkipChargeItemProcessingForPerDiem(data) {
    const procedureCodes = new Set();

    // Add codes from fee schedule data (which reference HealthcareServices by billingCode)
    if (data.feeScheduleData?.length) {
        data.feeScheduleData.forEach((feeSchedule) => {
            if (feeSchedule.charges?.length) {
                feeSchedule.charges.forEach((charge) => {
                    if (charge.billingCode) {
                        procedureCodes.add(charge.billingCode);
                    }
                });
            }
        });
    }

    // Add codes from updated services (HealthcareServices from charge master)
    if (data.updatedServices?.length) {
        data.updatedServices.forEach((service) => {
            if (service.billingCode) {
                procedureCodes.add(service.billingCode);
            }
        });
    }

    // Find perDiemProcedure by following encounter partOf chain
    const perDiemProcedure = await findPerDiemProcedureFromEncounters(data);

    // Check if any of the procedure codes match the per-diem applicable services
    if (perDiemProcedure?.perDiem?.applicableServices) {
        // Extract applicable procedure codes (same logic as PerDiemReport.vue)
        const applicableProcedureCodes = perDiemProcedure.perDiem.applicableServices.map((as) => as.code).filter(Boolean);

        // Check if any procedure code matches the applicable services (same logic as PerDiemReport.vue)
        const hasMatchingProcedure = Array.from(procedureCodes).some((procedureCode) => {
            return applicableProcedureCodes.includes(procedureCode);
        });

        if (hasMatchingProcedure) {
            return true;
        }
    }

    return false;
}

// Helper function to update procedure status when form is completed
const completeProceduresForEncounter = async function (encounterId, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (!encounterId) {
        return transaction;
    }

    try {
        // Find procedures associated with this encounter that are still in-progress
        const procedureResponse = await $httpFhirApi.get(`Procedure?encounter=${encounterId}&status=in-progress`);
        const procedures = procedureResponse.data?.entry?.map((e) => e.resource) || procedureResponse.data || [];

        // Update each procedure to completed status
        procedures.forEach((procedure) => {
            procedure.status = 'completed';
            transaction.push(crud.patch(procedure));
        });
    } catch (error) {
        console.error('Error updating procedure status:', error);
    }

    return transaction;
};

// Helper function to manage consent status updates for ROI forms
const updateConsentStatus = async function (questionnaireResponse, newStatus, existingTransaction = false) {
    let transaction = existingTransaction || [];

    try {
        const roiItem = questionnaireResponse.item?.find((item) => item.linkId === 'roi');
        if (!roiItem?.answer?.[0]?.value?.string) {
            return transaction;
        }

        if (newStatus === 'active') {
            const signers = questionnaireResponse.reviewer?.filter((r) => r.type === 'sign') || [];
            const allSignaturesSigned = signers.every((signer) => signer.signature && signer.when);

            if (signers.length > 0 && !allSignaturesSigned) {
                return transaction;
            }
        }

        // Parse the consent data from the answer
        const consentsData = JSON.parse(roiItem.answer[0].value.string);
        const consentArray = Array.isArray(consentsData) ? consentsData : [consentsData];

        // Batch-fetch all consents that need updating in one call
        const idsToFetch = consentArray
            .filter((c) => {
                if (!c.id) return false;
                return (newStatus === 'active' && c.status === 'proposed') || (newStatus === 'proposed' && c.status === 'active');
            })
            .map((c) => c.id);

        if (!idsToFetch.length) return transaction;

        const response = await $httpFhirApi.get(`Consent?_id=${idsToFetch.join(',')}&_elements=Consent.status`);
        const consents = Array.isArray(response.data) ? response.data : response.data?.entry?.map((e) => e.resource) || [];
        const consentMap = new Map();
        consents.forEach((consent) => {
            if (consent.id) consentMap.set(consent.id, consent);
        });

        // Update each consent to the new status
        for (const consentData of consentArray) {
            if (consentData.id && idsToFetch.includes(consentData.id)) {
                const consent = consentMap.get(consentData.id);
                if (consent) {
                    consent.status = newStatus;
                    transaction.push(crud.patch(consent));
                    consentData.status = newStatus;
                }
            }
        }

        // Update the questionnaireResponse with the modified consent data
        roiItem.answer[0].value.string = JSON.stringify(consentArray);
        transaction.push(crud.patch(questionnaireResponse));
    } catch (error) {
        console.error('Error updating consent status:', error);
    }

    return transaction;
};

// Main methods
async function lock(data, existingTransaction = false) {
    const isPortal = data.isPortal || false;
    let mainTransaction = existingTransaction || [];
    let observationTransactions = [];

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    const taskResults = await Promise.all(data.questionnaireResponses.map((qr) => getTasksForQuestionnaireResponse(qr.id)));

    let procedurePromises = [];

    for (let i = 0; i < data.questionnaireResponses.length; i++) {
        const questionnaireResponse = data.questionnaireResponses[i];
        const [task, assignments] = taskResults[i];
        // use copy of QuestionnaireResponse
        const qr = questionnaireResponse.getObjectCopy();

        // Lock form/questionnaireResponse
        mainTransaction = await status(
            {
                status: 'completed',
                questionnaireResponse: qr,
                user: data.user,
                task: task,
                assignments: assignments,
            },
            mainTransaction,
        );

        mainTransaction = await signerReviewer(
            {
                action: 'assign',
                task: task,
                questionnaireResponse: qr,
            },
            mainTransaction,
        );

        // this is commented out because it causes issues with assigning multiple signer tasks.
        // mainTransaction = uniqBy(mainTransaction, 'request.url');

        let transactions = await extractQuestionnaireResponse(
            {
                questionnaireResponse: qr,
                disabledItems: data.disabledItems,
            },
            [],
        );

        observationTransactions = [...observationTransactions, ...transactions];

        // Update procedure status to completed when form is finished
        const encounterId = qr.encounter?.id;
        if (encounterId && !isPortal) {
            procedurePromises.push(completeProceduresForEncounter(encounterId, []));
        }
    }

    if ((data.patients || data.activePatient) && !isPortal) {
        const qrsWithEncounters = data.questionnaireResponses.filter((qr) => qr.encounter?.id);

        const uniqueEncounterIds = [...new Set(qrsWithEncounters.map((qr) => qr.encounter.id))];
        const getEncounterIds = data.isGroupEncounter ? getGroupEncounterIds : getIndividualEncounterIds;
        const encounterIdResults = await Promise.all(uniqueEncounterIds.map((id) => getEncounterIds(id, [])));
        const allEncounterIds = [...new Set(encounterIdResults.flat())];

        let questionnaireResponsesWithoutClaims = qrsWithEncounters;
        if (allEncounterIds.length) {
            const claims = (await $httpFhirApi.get(`Claim?encounter=${allEncounterIds.join(',')}&_elements=status,supportingInfo`)).data;

            const referencedFormIds = new Set(
                claims
                    .filter((claim) => claim.status === 'active')
                    .flatMap((claim) => claim.supportingInfo || [])
                    .map((info) => info.code?.text?.match(/QuestionnaireResponse\/([^/\s]+)/)?.[1])
                    .filter(Boolean),
            );
            questionnaireResponsesWithoutClaims = qrsWithEncounters.filter((qr) => !referencedFormIds.has(qr.id));
        }

        // Process all questionnaire responses without claims
        if (questionnaireResponsesWithoutClaims.length > 0) {
            const shouldSkipChargeProcessing = await shouldSkipChargeItemProcessingForPerDiem(data);

            if (!shouldSkipChargeProcessing) {
                const { processChargeItemFlow } = useChargeItemFlow();
                await processChargeItemFlow({
                    ...data,
                    questionnaireResponses: questionnaireResponsesWithoutClaims,
                });
            }
        }
    }

    const procedureTransactions = await Promise.all(procedurePromises);
    mainTransaction = [...mainTransaction, ...procedureTransactions.flat()];

    if (existingTransaction === false) {
        return await Promise.all([postTransaction(mainTransaction), ...observationTransactions.map((transaction) => postTransaction(transaction))]);
    }

    return [];
}

async function unlock(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    for (let questionnaireResponse of data.questionnaireResponses) {
        // fetch associated tasks
        let [task, assignments] = await getTasksForQuestionnaireResponse(questionnaireResponse.id);

        // use copy of QuestionnaireResponse
        const qr = questionnaireResponse.getObjectCopy();

        transaction = await status(
            {
                status: 'in-progress',
                questionnaireResponse: qr,
                user: data.user,
                task: task,
                assignments: assignments,
            },
            transaction,
        );

        transaction = await removeAssignments(
            {
                assignments: assignments,
                type: ['signature', 'review'],
            },
            transaction,
        );

        transaction = await removeObservations(qr, transaction);

        const encounterId = questionnaireResponse.encounter?.id;

        // Clean up charge items
        const isGroupEncounter = encounterId ? !!task.groupTask : false;
        transaction = await cleanupChargeItemsForQuestionnaireResponse(questionnaireResponse.id, encounterId, transaction, isGroupEncounter);

        // Update consent status to proposed when ROI form is unlocked
        transaction = await updateConsentStatus(qr, 'proposed', transaction);
    }

    transaction = uniqBy(transaction, 'request.url');

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function unlockSignatureReview(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    for (let questionnaireResponse of data.questionnaireResponses) {
        let [task, assignments] = await getTasksForQuestionnaireResponse(questionnaireResponse.id);

        const hasSignatureOrReviewAssignments = assignments.some((assignment) => assignment.code.coding.some((coding) => coding.version === 'signature' || coding.version === 'review'));

        // If the response has signers/reviewers, remove them and assign new tasks
        if (hasSignatureOrReviewAssignments) {
            transaction = await removeAssignments(
                {
                    assignments: assignments,
                    type: ['signature', 'review'],
                },
                transaction,
            );

            transaction = uniqBy(transaction, 'request.url');

            transaction = await signerReviewer(
                {
                    action: 'assign',
                    task: task,
                    questionnaireResponse: questionnaireResponse.getObjectCopy(),
                },
                transaction,
            );
        }
    }

    if (existingTransaction === false) {
        await postTransaction(transaction);
    }

    return transaction;
}

async function updateSignerReviewer(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    let questionnaireResponses = await $httpFhirApi.get('/QuestionnaireResponse?id=' + data.qrIds.join(','));

    // do same thing for all QRs @TODO later change this to be patient specific when we add patient signing to group notes
    for (let questionnaireResponse of questionnaireResponses.data) {
        // fetch associated tasks
        let [task, assignments] = await getTasksForQuestionnaireResponse(questionnaireResponse.id);
        // use copy of QuestionnaireResponse
        const qr = questionnaireResponse.getObjectCopy();

        // For patients, we want to make sure reviewers are tied to the right QuestionnaireResponse
        let reviewers = data.reviewers.filter((item) => item.reviewer.who?.resourceType !== 'Patient' || [item.relatedTo, item.reviewer.who?.id].includes(qr.subject?.id));

        transaction = await signerReviewer(
            {
                action: 'update',
                task: task,
                assignments: assignments,
                questionnaireResponse: qr,
                reviewer: reviewers.map((item) => item.reviewer),
            },
            transaction,
        );
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

const signReview = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];
    const authStore = useAuthStore();
    let ip = await axios.get('https://api.ipify.org?format=json');

    if (data.questionnaireResponse) {
        data.questionnaireResponses = [data.questionnaireResponse];
    }

    const taskResults = await Promise.all(data.questionnaireResponses.map((qr) => getTasksForQuestionnaireResponse(qr.id)));

    for (let i = 0; i < data.questionnaireResponses.length; i++) {
        const qr = data.questionnaireResponses[i];
        const assignments = taskResults[i][1];

        // use copy of QuestionnaireResponse
        const questionnaireResponse = qr.getObjectCopy();

        // find/update the reviewer entry
        const reviewer = questionnaireResponse.reviewer?.find((item) => item.who.id === data.actor.id);
        if (reviewer) {
            reviewer.when = timezoneApi.getLocalDateTime(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", Intl.DateTimeFormat().resolvedOptions().timeZone);
            if (data.action === 'signature' && data.signatureProps) {
                reviewer.context = data.signatureProps?.context;
                reviewer.format = data.signatureProps?.format;
                reviewer.signature = data.signatureProps?.signature;
                reviewer.ip = ip?.data?.ip;
                reviewer.user = authStore.aidboxPersonReference;
                reviewer.jobTitle = data.signatureProps?.jobTitle;
                reviewer.credentials = data.signatureProps?.credentials;
            }
            transaction.push(crud.patch(questionnaireResponse));
        } else continue;

        // update associated task
        const task = assignments?.find((task) => task.owner.id === data.actor.id && task.code.coding[0]?.version === data.action);
        if (task) {
            task.status = 'completed';
            if (!task.executionPeriod) {
                task.executionPeriod = { end: '' };
            }
            task.executionPeriod.end = new Date().toISOString();
            transaction.push(crud.patch(task));
        }

        // After signing, check if all signatures are complete and update consent status if needed
        if (data.action === 'signature') {
            transaction = await updateConsentStatus(questionnaireResponse, 'active', transaction);
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

async function initializeQuestionnaire(data, existingTransaction = false) {
    if (!data?.questionnaireResponse?.id) {
        return existingTransaction || [];
    }

    let transaction = existingTransaction || [];
    // fetch associated tasks
    let subtasks = await getSubtasks(data.task.id);

    // update status of task & assignments
    data.task.status = 'in-progress';
    data.task.focus = { id: data.questionnaireResponse.id, display: data.task.focus?.display, resourceType: 'QuestionnaireResponse' };
    transaction.push(crud.patch(data.task));
    subtasks.forEach((task) => {
        task.status = 'in-progress';
        transaction.push(crud.patch(task));
    });

    let formEncounter;
    let encounterFormID = uuidv4();
    let qrHasSessionWidget = data.questionnaireResponse.item.some((e) => e.linkId === 'encounterDetails');

    if (qrHasSessionWidget && data.encounter) {
        if (!data.encounter) return;

        formEncounter = new Encounter({
            id: encounterFormID,
            status: 'in-progress',
            code: data.encounter.code,
            class: data.encounter.class,
            period: data.encounter.period,
            subject: data.encounter.subject,
            location: data.encounter.location,
            diagnosis: data.encounter.diagnosis,
            appointment: data.encounter.appointment,
            participant: data.encounter.participant,
            episodeOfCare: data.encounter.episodeOfCare,
            partOf: { id: data.encounter.id, resourceType: 'Encounter' },
            type: [{ text: 'formEncounter' }],
        });

        transaction.push(crud.post(formEncounter));
        data.questionnaireResponse.encounter = { id: encounterFormID, resourceType: 'Encounter' };
        transaction.push(crud.patch(data.questionnaireResponse));

        if (data.appointmentType?.id) {
            const appointmentResponse = (await $httpFhirApi.get('/BN_EncounterType?id=' + data.appointmentType?.id + '&_include=service')).data;
            const appointmentType = appointmentResponse.find((r) => r.resourceType === 'BN_EncounterType');

            const currentFormUrl = getUrlFromCanonical(data.questionnaireResponse.questionnaire)?.url;
            const currentFormEntry = appointmentType?.form?.find((e) => e.url === currentFormUrl);

            const appointmentTypeService = currentFormEntry
                ? [currentFormEntry]
                      .map((e) => {
                          if (e.service?.display === 'non-billable') {
                              return {
                                  id: 'non-billable',
                                  code: { coding: [{ display: 'Non-billable', version: 'non-billable' }] },
                                  perUnitDuration: 0,
                              };
                          }
                          if (e.service?.id) {
                              return appointmentResponse.find((r) => r.id === e.service.id);
                          }
                      })
                      .filter(Boolean)
                : [];

            transaction = await addProcedure(
                {
                    patient: data.encounter.subject,
                    service: appointmentTypeService,
                    encounter: formEncounter,
                },
                transaction,
            );
        } else if (data.service?.length) {
            transaction = await addProcedure(
                {
                    patient: data.encounter.subject,
                    service: data.service,
                    encounter: formEncounter,
                },
                transaction,
            );
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}
const addProcedure = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (!data.service?.length) return transaction;

    const encounter = data.encounter?.id ? { resourceType: 'Encounter', id: data.encounter.id } : undefined;
    // Handle non-billable services first (id is non-billable because it's AppointmentService)
    const nonBillableServices = data.service.filter((ser) => ser.id === 'non-billable');

    for (const service of nonBillableServices) {
        let resource = new Procedure({
            id: uuidv4(),
            resourceType: 'Procedure',
            code: {
                coding: [
                    {
                        display: 'Non-billable',
                        version: 'non-billable',
                    },
                ],
            },
            status: 'in-progress',
            subject: data.patient,
            encounter: {
                id: encounter?.id,
                resourceType: 'Encounter',
            },
            perUnitDuration: 0,
        });
        transaction.push(crud.post(resource));
    }

    // Handle regular services
    const regularServices = data.service.filter((ser) => ser.id !== 'non-billable');

    if (regularServices.length > 0) {
        const addOnServicesIds = regularServices
            .filter((ser) => ser.addOn?.length > 0)
            .flatMap((ser) => ser.addOn?.filter((a) => a.includeByDefault).map((a) => a.addOnCode?.id))
            .filter(Boolean);

        const [hcsResult, addOnServicesResult] = await Promise.all([
            $httpFhirApi.get(
                'Encounter?id=' +
                    data.encounter.partOf?.id +
                    '&_include:iterate=Encounter:part-of:Encounter&_include=Encounter:healthcareService&_elements=Encounter.id,HealthcareService.defaultModifier',
            ),
            addOnServicesIds.length > 0 ? $httpFhirApi.get('HealthcareService?id=' + addOnServicesIds.join(',')) : Promise.resolve({ data: [] }),
        ]);

        const addOnServices = addOnServicesResult.data;
        for (const service of regularServices) {
            const procedureId = uuidv4();
            const subject = data.patient;

            // Find the corresponding service from the data passed in
            let resource = new Procedure({
                id: procedureId,
                status: 'in-progress',
                encounter: encounter,
                subject: subject,
                code: { text: service.description, coding: [{ code: service.billingCode, display: service.officeCode || service.name, version: service.id }] },
                unit: service.unit || 1,
                // Only use selected modifiers from the selected service
                modifier: service?.modifier?.filter((m) => m?.includeByDefault) || [],
            });

            // Determine if there is a ProgramDefaultModifier
            let healthcareService = hcsResult.data.find((r) => r.resourceType === 'HealthcareService');

            if (healthcareService?.defaultModifier) {
                if (!resource.modifier.some((existingModifier) => existingModifier.code === healthcareService.defaultModifier && existingModifier.position === 3)) {
                    resource.modifier.push({ code: healthcareService.defaultModifier, position: 3 });
                }
            }

            // Auto-select user's practitioner modifier when setting a new service
            const practitionerModifier = useAuthStore().practitioner.practitionerModifier;
            if (practitionerModifier && !resource.modifier.some((m) => m.code === practitionerModifier.code && m.position === 1)) {
                resource.modifier.push(new ProcedureModifier({ code: practitionerModifier.code, position: 1 }));
            }

            transaction.push(crud.post(resource));

            // sub-procedures (addOn services)
            service.addOn?.forEach((addOn) => {
                const addOnService = addOnServices.find((s) => s.id === addOn.addOnCode?.id);
                if (!addOnService) return;
                let subResource = new Procedure({
                    id: uuidv4(),
                    status: 'in-progress',
                    encounter: encounter,
                    subject: subject,
                    partOf: [{ id: procedureId, resourceType: 'Procedure' }],
                    code: { text: addOnService.name, coding: [{ code: addOnService.billingCode, display: addOnService.name, version: addOnService.id }] },
                    unit: addOn.unit || 1,
                    modifier: addOn.modifier || [],
                });
                transaction.push(crud.post(subResource));
            });
        }

        if (existingTransaction === false) {
            return await postTransaction(transaction);
        }
    }
    return transaction;
};

async function blankQuestionnaire(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const authStore = useAuthStore();

    let newForm = new Questionnaire({
        id: uuidv4(),
        resourceType: 'Questionnaire',
        title: data.type === 'live' ? 'Live Form' : 'New Form',
        status: 'draft',
        version: '1',
        subjectType: ['Patient'],
        formType: data.type,
        bnOwner: { id: authStore.organizationAccount.id, resourceType: 'Organization' },
    });

    newForm.url = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri + '/Questionnaire/' + newForm.id;
    transaction.push(crud.post(newForm));
    // add to collection if specified
    if (data.collection) {
        const item = {
            code: 'Form',
            name: 'Form',
            value: {
                uri: newForm.url,
            },
        };
        data.collection.item.push(item);

        transaction.push(crud.patch(data.collection));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function publishQuestionnaire(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    let existing = await $httpFhirApi.get('Questionnaire?url=' + data.questionnaire.url + '&status=active,draft,retired');
    if (existing.data.length > 0) {
        for (const form of existing.data) {
            // 'publish' - set current form to 'active'
            if (form.id === data.questionnaire.id) {
                form.status = 'active';
                // push update to any descendant forms if any (sync with parent)

                // const { updateDescendantForms } = useProfileFlow();
                // transaction = await updateDescendantForms({ form: form }, transaction);
            } else {
                form.status = form.status === 'draft' ? 'draft' : 'unknown';
            }
            transaction.push(crud.patch(form));
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function copyQuestionnaire(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const authStore = useAuthStore();

    // fetch the questionnaire if a url is provided
    if (data.url) {
        const [profileServer] = data.url.split('/Questionnaire/');
        let managedForm = await $http.get(profileServer + '/Questionnaire?_sort=-version&_count=1&status=active&url=' + data.url);
        managedForm = managedForm.data?.entry?.[0]?.resource;
        if (managedForm.id) {
            data.questionnaire = new Questionnaire(managedForm);
        }
    }
    let clone = data.questionnaire.toJSON();
    clone.id = uuidv4();
    clone.status = 'draft';
    clone = new Questionnaire(clone);

    // make standalone
    if (data.decouple) {
        clone.derivedFrom = [];
        clone.publisher = undefined;
        unlockFormItems(clone.item);
    }

    // set the bnOwner based on results of confirmation (defaults to brand)
    if (data.owner === 'account') {
        clone.bnOwner = new Reference({ id: authStore.account.id, resourceType: 'Organization' });
    } else {
        clone.bnOwner = new Reference({ id: authStore.brand.id, resourceType: 'Organization' });
    }
    // set additional properties
    clone.title = clone.title + ' - COPY';
    clone.url = import.meta.env.VITE_PROTOCOL + authStore.account.fhirApiUri + '/Questionnaire/' + clone.id;
    clone.version = '1';

    // save the cloned form
    transaction.push(crud.post(clone));

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogInformationText(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        let needsUpdate = false;

        for (let item of fields) {
            if (item.informationText !== data.informationText) {
                needsUpdate = true;
                item.informationText = data.informationText;
            }
        }

        // Only increment version if content actually changed
        if (needsUpdate && form.status === 'active') {
            await incrementQuestionnaireMinorVersion(form, false);
        }

        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogEhrOnly(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        let needsUpdate = false;

        for (let item of fields) {
            if (item.ehrOnly !== data.ehrOnly) {
                needsUpdate = true;
                item.ehrOnly = data.ehrOnly;
            }
        }

        // Only increment version if content actually changed
        if (needsUpdate && form.status === 'active') {
            await incrementQuestionnaireMinorVersion(form, false);
        }

        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogPortalReadOnly(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        let needsUpdate = false;

        for (let item of fields) {
            if (item.portalReadOnly !== data.portalReadOnly) {
                needsUpdate = true;
                item.portalReadOnly = data.portalReadOnly;
            }
        }

        // Only increment version if content actually changed
        if (needsUpdate && form.status === 'active') {
            await incrementQuestionnaireMinorVersion(form, false);
        }

        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogDeprecate(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);
        let needsUpdate = false;

        for (let item of fields) {
            if (item.deprecate !== data.deprecate) {
                needsUpdate = true;
                item.deprecate = data.deprecate;
            }
        }

        // Only increment version if content actually changed
        if (needsUpdate && form.status === 'active') {
            await incrementQuestionnaireMinorVersion(form, false);
        }

        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogRequirementType(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        let fields = form.getCatalogFields(data.code);

        for (let item of fields) {
            item.isCarf = data.isCarf;
            item.isJointCommision = data.isJointCommision;
            item.isCms = data.isCms;
        }
        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogText(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        await incrementQuestionnaireMinorVersion(form, false);
        let fields = form.getCatalogFields(data.code);

        for (let item of fields) {
            item.bnText = data.text;
        }
        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogType(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?&status=active,draft&questionnaireField=' + data.code);

    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    for (let form of uniqueQuestionnaires) {
        await incrementQuestionnaireMinorVersion(form, false);
        let fields = form.getCatalogFields(data.code);

        for (let item of fields) {
            item.type = data.type;
        }
        transaction.push(crud.patch(form));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateCatalogCode(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let resources = await $httpFhirApi.get('Questionnaire?questionnaireField=' + data.id);
    let codes = data.code.filter((e) => e.code)?.length ? data.code.filter((e) => e.code) : [];
    let questionnaires = resources.data;
    let uniqueQuestionnaires = uniqBy(questionnaires, 'id').filter((e) => e);

    uniqueQuestionnaires.forEach((form) => {
        let fields = form.getCatalogFields(data.id);
        fields.forEach((item) => {
            let questionnaireFieldCode = item.code.find((e) => e.system === 'http://fhir.bestnotes.com/BN_QuestionnaireField');
            item.code = codes?.length ? [...codes, questionnaireFieldCode] : [questionnaireFieldCode];
        });
        transaction.push(crud.patch(form));
    });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function updateChoicesFromCatalog(data, existingTransaction = false) {
    let transaction = existingTransaction || [];
    let formsRequest = await $httpFhirApi.get('Questionnaire?status=active,draft&questionnaireField=' + data.catalog.id);
    let uniqueQuestionnaires = uniqBy(formsRequest.data, 'id').filter((e) => e);

    for (let questionnaire of uniqueQuestionnaires) {
        transaction = await incrementQuestionnaireMinorVersion(questionnaire, transaction);

        if (questionnaire.id !== data.formId) {
            let items = questionnaire.getCatalogFields(data.catalog.id);

            for (let item of items) {
                item.answerOption = data.catalog.choice?.map((e) => new QuestionnaireAnswerOption({ score: e.score, value: { Coding: { display: e.display, code: e.code } } }));
            }
        }
        transaction.push(crud.patch(questionnaire));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function incrementQuestionnaireMinorVersion(questionnaire, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (questionnaire.status !== 'active') return transaction;

    let questionnaireCopy = questionnaire.toJSON();
    let version = questionnaire.version.split('.');
    let newVersion;

    questionnaireCopy.id = uuidv4();
    questionnaireCopy = new Questionnaire(questionnaireCopy);
    questionnaireCopy.status = 'retired';

    if (!version[1]) {
        newVersion = version[0] + '.1';
    } else {
        newVersion = version[0] + '.' + (parseInt(version[1]) + 1);
    }
    questionnaire.version = newVersion;

    transaction.push(crud.patch(questionnaire));
    transaction.push(crud.post(questionnaireCopy));

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function getObservations(items, subject, existingTransaction = false) {
    const durationTypeMap = {
        a: 'years',
        mo: 'months',
        wk: 'weeks',
        d: 'days',
        h: 'hours',
        "'": 'minutes',
        "''": 'seconds',
    };

    // Filter to only items with observationLinkPeriod and code upfront
    const validItems = items.filter((item) => item.observationLinkPeriod && item.code);

    // Early return if no valid items
    if (validItems.length === 0) {
        if (existingTransaction === false) {
            return {};
        }
        return existingTransaction || [];
    }

    const now = new Date();

    // Separate items that need _include=has-member (must remain individual FHIR queries)
    const questionItems = validItems.filter((item) => item.type === 'question');
    const batchableItems = validItems.filter((item) => item.type !== 'question');

    // Group batchable items by date constraint to minimize AidboxQuery calls
    // Key: minDate string, Value: { items: [], codes: Set }
    const dateGroups = new Map();

    for (const item of batchableItems) {
        const durationKey = durationTypeMap[item.observationLinkPeriod.code];
        const date = sub(now, { [durationKey]: parseInt(item.observationLinkPeriod.value) });
        const minDate = format(date, 'yyyy-MM-dd');

        if (!dateGroups.has(minDate)) {
            dateGroups.set(minDate, { items: [], codes: new Set(), linkIdToCodeKeys: new Map() });
        }

        const group = dateGroups.get(minDate);
        group.items.push(item);

        // Track which codes belong to which linkId
        const codeKeys = [];
        for (const coding of item.code) {
            if (coding.system !== 'http://fhir.bestnotes.com/BN_QuestionnaireField') continue;
            group.codes.add(coding.code);
            codeKeys.push(coding.code);
        }
        group.linkIdToCodeKeys.set(item.linkId, codeKeys);
    }

    // Build results
    const returnResults = {};

    // Process batched items using AidboxQuery (parallel calls for each date group)
    const dateGroupEntries = Array.from(dateGroups.entries());

    const batchPromises = dateGroupEntries.map(([minDate, group]) => {
        const codesString = Array.from(group.codes).join(',');
        return $httpFhirApi
            .get(`/$query/most-recent-observations-per-code`, {
                params: {
                    subject_id: subject.id,
                    codes: codesString,
                    min_date: minDate,
                },
            })
            .then((response) => ({ success: true, response, group }))
            .catch((error) => ({ success: false, error, group }));
    });

    const batchResults = await Promise.all(batchPromises);

    // Process batch results
    for (const result of batchResults) {
        const { group } = result;

        if (result.success) {
            // Build a map of code -> array of observation values
            // Query returns: id, value, code, effective_date, performer, derived_from
            // Multiple rows can exist per code (e.g., multi-select choice items)
            const codeToValues = new Map();
            for (const row of result.response.originalData.data || []) {
                if (row.value) {
                    const entry = { value: row.value, performer: row.performer, derivedFrom: row.derived_from, instant: row.effective_date };
                    if (codeToValues.has(row.code)) {
                        codeToValues.get(row.code).push(entry);
                    } else {
                        codeToValues.set(row.code, [entry]);
                    }
                }
            }

            // Map results back to each item's linkId
            for (const item of group.items) {
                const itemCodeKeys = group.linkIdToCodeKeys.get(item.linkId);

                if (item.type === 'choice') {
                    // For choice items, return all matching values for any of the item's codes
                    const matchingValues = [];
                    for (const codeKey of itemCodeKeys) {
                        if (codeToValues.has(codeKey)) {
                            matchingValues.push(...codeToValues.get(codeKey));
                        }
                    }
                    returnResults[item.linkId] = matchingValues;
                } else {
                    // For other items, return the first value for any of the item's codes
                    let found = false;
                    for (const codeKey of itemCodeKeys) {
                        if (codeToValues.has(codeKey)) {
                            returnResults[item.linkId] = codeToValues.get(codeKey);
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        returnResults[item.linkId] = [];
                    }
                }
            }
        } else {
            console.error('Error fetching observations via AidboxQuery:', result.error);
            // On error, set empty results for all items in this group
            for (const item of group.items) {
                returnResults[item.linkId] = [];
            }
        }
    }

    // Process question items individually (need _include=has-member)
    if (questionItems.length > 0) {
        let transaction = existingTransaction || [];
        const questionIndexMap = {};
        let index = transaction.length;
        const baseParams = `subject=${subject.id}&status=final,amended,corrected&_sort=-date`;

        for (const item of questionItems) {
            const codesForQueryString = item.code.map((coding) => `${coding.system}|${coding.code}`).join(',');
            const durationKey = durationTypeMap[item.observationLinkPeriod.code];
            const date = sub(now, { [durationKey]: parseInt(item.observationLinkPeriod.value) });
            const dateString = 'ge' + format(date, 'yyyy-MM-dd');

            const queryString = `/Observation?${baseParams}&code=${codesForQueryString}&date=${dateString}&_count=1&_include=has-member`;

            transaction.push({
                request: {
                    method: 'GET',
                    url: queryString,
                },
            });
            questionIndexMap[item.linkId] = index++;
        }

        if (existingTransaction === false) {
            const results = await postTransaction(transaction);

            for (const linkId in questionIndexMap) {
                const idx = questionIndexMap[linkId];
                const entries = results[idx]?.entry || [];

                if (entries.length <= 1) {
                    returnResults[linkId] = entries;
                } else {
                    // Filter entries so Observations are "derivedFrom" the same place
                    const derivedFromIds = entries[0]?.derivedFrom?.map((from) => from?.id) || [];
                    returnResults[linkId] = entries.filter((ob) => ob.derivedFrom?.some((dr) => derivedFromIds.includes(dr.id)));
                }
            }
        } else {
            // If using existing transaction, return the transaction for later processing
            return transaction;
        }
    }

    return returnResults;
}

async function syncFieldSetAcrossForms(data, existingTransaction = false) {
    // Validate that this is actually a field set with items
    if (!data.fieldSet || !data.fieldSet.item || !data.fieldSet.item.length) {
        return existingTransaction || [];
    }

    let transaction = existingTransaction || [];

    // Query for all questionnaires that contain this field set
    let formsRequest = await $httpFhirApi.get('Questionnaire?status=active,draft&questionnaireField=' + data.fieldSet.id);
    let uniqueQuestionnaires = uniqBy(formsRequest.data, 'id').filter((e) => e);

    for (let questionnaire of uniqueQuestionnaires) {
        // Skip the current form being edited
        if (questionnaire.id === data.formId) {
            continue;
        }

        // Find all instances of this field set in the questionnaire
        let fieldSets = questionnaire.getCatalogFields(data.fieldSet.id);

        // Only proceed if we found field sets to update in this questionnaire
        if (fieldSets && fieldSets.length > 0) {
            // Version active forms since field structure is changing
            if (questionnaire.status === 'active') {
                transaction = await incrementQuestionnaireMinorVersion(questionnaire, transaction);
            }

            // Update all instances of the field set
            for (let fieldSet of fieldSets) {
                // Create a new array with deep copies of the items to avoid reference issues
                fieldSet.item = JSON.parse(JSON.stringify(data.fieldSet.item || []));
            }

            // Add to transaction
            transaction.push(crud.patch(questionnaire));
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}
const getIndividualEncounterIds = async (encounterId, allEncounters = []) => {
    const allIds = new Set([encounterId]);

    if (allEncounters.length > 0) {
        const currentEncounter = allEncounters.find((e) => e.id === encounterId);
        if (currentEncounter) {
            const childEncounters = allEncounters.filter((e) => e.resourceType === 'Encounter' && e.partOf?.id === encounterId);
            childEncounters.forEach((child) => {
                if (child.id) {
                    allIds.add(child.id);
                }
            });

            if (currentEncounter.partOf?.id) {
                const parentId = currentEncounter.partOf.id;
                const siblings = allEncounters.filter((e) => e.resourceType === 'Encounter' && e.partOf?.id === parentId && e.id !== encounterId);
                siblings.forEach((sibling) => {
                    if (sibling.id) {
                        allIds.add(sibling.id);
                    }
                });
            }
        }
    } else {
        const response = await $httpFhirApi.get(`Encounter?_id=${encounterId}&_include=Encounter:part-of&_revinclude:iterate=Encounter:part-of&_elements=Encounter.id`);
        const encounters = response.data || [];
        encounters.forEach((e) => {
            if (e.id) allIds.add(e.id);
        });
    }

    return Array.from(allIds);
};

const getGroupEncounterIds = async (encounterId, allEncounters = []) => {
    const allIds = new Set([]);

    if (allEncounters.length > 0) {
        const currentEncounter = allEncounters.find((e) => e.id === encounterId);
        if (currentEncounter) {
            allIds.add(encounterId);
            const childEncounters = allEncounters.filter((e) => e.resourceType === 'Encounter' && e.partOf?.id === encounterId);
            childEncounters.forEach((child) => {
                if (child.id) {
                    allIds.add(child.id);
                }
            });
        }
    } else {
        const response = await $httpFhirApi.get(`Encounter?_id=${encounterId}&_include:iterate=Encounter:part-of:Encounter&_revinclude=Encounter:part-of&_elements=Encounter.id`);
        const encounterIds = response.data.map((e) => e.id);
        allIds.add(...encounterIds);
    }

    return Array.from(allIds);
};

const checkFormsHaveSubmittedClaims = async (formIds, encounterId = null, allEncounters = [], isGroupEncounter = false) => {
    const actualFormIds = Array.isArray(formIds) ? formIds : [formIds];

    if (!actualFormIds.length || !encounterId) {
        return [];
    }

    // Use appropriate function based on encounter type
    const allEncounterIds = isGroupEncounter ? await getGroupEncounterIds(encounterId, allEncounters) : await getIndividualEncounterIds(encounterId, allEncounters);

    if (!allEncounterIds.length) {
        return [];
    }

    // Fetch claims for all encounters in a single request
    const claims = (await $httpFhirApi.get(`Claim?encounter=${allEncounterIds.join(',')}&_elements=status,supportingInfo`)).data;

    // Extract all QuestionnaireResponse references from active claims' supportingInfo
    const referencedFormIds = new Set(
        claims
            .filter((claim) => claim.status === 'active')
            .flatMap((claim) => claim.supportingInfo || [])
            .map((info) => info.code?.text?.match(/QuestionnaireResponse\/([^/\s]+)/)?.[1])
            .filter(Boolean),
    );

    // Return only the form IDs that were passed in and have claims
    return actualFormIds.filter((formId) => referencedFormIds.has(formId));
};

export function useQuestionnaireFlow() {
    return {
        lock,
        unlock,
        updateSignerReviewer,
        signReview,
        initializeQuestionnaire,
        blankQuestionnaire,
        publishQuestionnaire,
        copyQuestionnaire,
        updateCatalogInformationText,
        updateCatalogText,
        updateCatalogType,
        updateCatalogCode,
        updateCatalogEhrOnly,
        updateCatalogPortalReadOnly,
        updateCatalogRequirementType,
        findQuestionnaireItemByLinkId,
        filterQuestionnaireItemByLinkId,
        findQuestionnaireItem,
        filterQuestionnaireItems,
        extractQuestionnaireResponse,
        incrementQuestionnaireMinorVersion,
        getObservations,
        removeObservations,
        updateChoicesFromCatalog,
        updateCatalogDeprecate,
        unlockSignatureReview,
        syncFieldSetAcrossForms,
        getIndividualEncounterIds,
        getGroupEncounterIds,
        cleanupChargeItemsForQuestionnaireResponse,
        checkFormsHaveSubmittedClaims,
        addProcedure,
        completeProceduresForEncounter,
    };
}
